function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React from 'react';
import lastDayOfMonth from 'date-fns/lastDayOfMonth';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Typography } from 'seagull';
import CustomLegend from "../charts/CustomLegend/CustomLegend";
import CustomTooltip from "../charts/CustomTooltip/CustomTooltip";
import CustomBarShape from "../charts/CustomBarShape/CustomBarShape";
import CustomXAxisTick from "../charts/CustomXAxisTick/CustomXAxisTick";
import { chartColors } from "../charts/chartColors";
import { commonXAxisProps, commonBarChartProps, commonCartesianGridProps, commonBarProps } from "../charts/commonChartProps";
import styles from "./MonthlyEarningsWidget.module.scss";
import { utils } from "../../../utils";
const legendItems = [{
  label: 'In your bank account',
  color: chartColors.green
}, {
  label: 'Collected successfully',
  color: chartColors.blue
}, {
  label: 'Pending verification',
  color: chartColors.lightBlue
}, {
  label: 'Future payments',
  color: chartColors.lightGray
}];

const yAxisTickFormatter = value => {
  if (value === 0) {
    return '';
  }

  return utils.formatDollarAmount(value);
};

const getTooltipItems = payload => {
  return payload === null || payload === void 0 ? void 0 : payload.map(p => ({
    id: p.dataKey,
    label: utils.formatDollarAmount(p.value),
    color: p.color
  }));
};

const daysWithXAxisTickValue = new Set([1, 5, 10, 15, 20, 25]);

const getXAxisTickText = props => {
  const {
    index,
    payload
  } = props;
  const {
    value
  } = payload;
  const day = index + 1;
  const now = new Date();
  const isNearToday = Math.abs(day - now.getDate()) === 1;
  const isLastDayOfMonth = day === lastDayOfMonth(now).getDate();
  const showValue = !isNearToday && (daysWithXAxisTickValue.has(day) || isLastDayOfMonth);
  return value === 'Today' ? /*#__PURE__*/React.createElement(Typography, {
    variant: "paragraph2",
    skin: "standard"
  }, value) : showValue ? value : /*#__PURE__*/React.createElement("div", {
    className: styles.dot
  });
};

const paidHasRadius = data => !data.in_transit && !data.clear && !data.future;

const inTransitHasRadius = data => !data.clear && !data.future;

const clearHasRadius = data => !data.future;

export default function MonthlyEarningsChart(props) {
  const {
    chartData
  } = props;
  return /*#__PURE__*/React.createElement(ResponsiveContainer, {
    width: "100%",
    height: "100%"
  }, /*#__PURE__*/React.createElement(BarChart, _extends({}, commonBarChartProps, {
    data: chartData
  }), /*#__PURE__*/React.createElement(CartesianGrid, commonCartesianGridProps), /*#__PURE__*/React.createElement(XAxis, _extends({}, commonXAxisProps, {
    dataKey: "date",
    height: 49,
    tick: /*#__PURE__*/React.createElement(CustomXAxisTick, {
      tickWidth: 50,
      tickHeight: 30,
      getText: getXAxisTickText,
      testid: "monthly-earnings-chart-x-tick"
    })
  })), /*#__PURE__*/React.createElement(YAxis, {
    axisLine: false,
    tick: {
      fill: chartColors.axisTicks
    },
    tickLine: false,
    tickFormatter: yAxisTickFormatter,
    fontSize: 12
  }), /*#__PURE__*/React.createElement(Tooltip, {
    cursor: false,
    content: /*#__PURE__*/React.createElement(CustomTooltip, {
      getItems: getTooltipItems
    })
  }), /*#__PURE__*/React.createElement(Legend, {
    content: /*#__PURE__*/React.createElement(CustomLegend, {
      testid: "monthly-earnings-chart-legend",
      items: legendItems
    })
  }), /*#__PURE__*/React.createElement(ReferenceLine, {
    x: "Today",
    stroke: chartColors.axisLine,
    strokeDasharray: "6 6"
  }), /*#__PURE__*/React.createElement(Bar, _extends({}, commonBarProps, {
    dataKey: "paid",
    stackId: "a",
    fill: chartColors.green,
    shape: /*#__PURE__*/React.createElement(CustomBarShape, {
      testid: "monthly-earnings-paid",
      getHasRadius: paidHasRadius,
      linkTarget: "_blank"
    })
  })), /*#__PURE__*/React.createElement(Bar, _extends({}, commonBarProps, {
    dataKey: "in_transit",
    stackId: "a",
    fill: chartColors.blue,
    shape: /*#__PURE__*/React.createElement(CustomBarShape, {
      testid: "monthly-earnings-in-transit",
      getHasRadius: inTransitHasRadius,
      linkTarget: "_blank"
    })
  })), /*#__PURE__*/React.createElement(Bar, _extends({}, commonBarProps, {
    dataKey: "clear",
    stackId: "a",
    fill: chartColors.lightBlue,
    shape: /*#__PURE__*/React.createElement(CustomBarShape, {
      testid: "monthly-earnings-clear",
      getHasRadius: clearHasRadius,
      linkTarget: "_blank"
    })
  })), /*#__PURE__*/React.createElement(Bar, _extends({}, commonBarProps, {
    dataKey: "future",
    stackId: "a",
    fill: chartColors.lightGray,
    shape: /*#__PURE__*/React.createElement(CustomBarShape, {
      hasRadius: true,
      testid: "monthly-earnings-future"
    })
  }))));
}